import axios from 'axios';
import { notifyServer } from '~/utils/lipSyncNotify';
import {
  applySecretResponseHeaders,
  getRequestAuthorization,
  getRequestIdentityId,
  getRequestWapuId,
  getSigningHeadersForRequest,
  isLoginPromptSuppressed,
  promptLoginForCode,
  reportAutomationBeforeCreation,
  resolveRequestPath,
  serializeRequestBody,
  shouldSignRequest,
  WAPU_ID_HEADER,
} from '~/utils/requestVerification';

const Msgs = {
  200: 'Operation successful',
  401: 'No permission to operate',
  500: 'Internal system error',
};

let isLoginPrompting = false;

const instance = axios.create({
  baseURL: process.env.NUXT_PUBLIC_API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
});

function setHeader(headers, key, value) {
  if (!value) return;
  if (typeof headers.set === 'function') {
    headers.set(key, value);
    return;
  }
  headers[key] = value;
}

function promptLoginOnce(payload) {
  if (
    !payload ||
    Number(payload.code) !== 100001 ||
    isLoginPrompting ||
    isLoginPromptSuppressed()
  )
    return;
  isLoginPrompting = true;
  promptLoginForCode(payload);
  setTimeout(() => {
    isLoginPrompting = false;
  }, 800);
}

instance.interceptors.request.use(async (config) => {
  config.headers = config.headers || {};

  const authorization = getRequestAuthorization();
  if (authorization) {
    setHeader(config.headers, 'Authorization', authorization);
  }

  const wapuId = getRequestWapuId();
  if (wapuId) {
    setHeader(config.headers, WAPU_ID_HEADER, wapuId);
  }

  const path = resolveRequestPath(
    config.url || '',
    config.baseURL || instance.defaults.baseURL || '',
  );
  const method = (config.method || 'GET').toUpperCase();

  const identityId = getRequestIdentityId();

  if (identityId && path.startsWith('/api/aigc/')) {
    setHeader(config.headers, 'X-Identity-Id', identityId);
  }

  if (!config._automationReported) {
    await reportAutomationBeforeCreation(path);
    config._automationReported = true;
  }

  if (shouldSignRequest(path)) {
    const { body, normalizedData, shouldReplaceData } = serializeRequestBody(config.data);
    if (shouldReplaceData) {
      config.data = normalizedData;
    }

    const signingHeaders = await getSigningHeadersForRequest(
      method,
      path,
      body,
      identityId,
    );
    Object.entries(signingHeaders).forEach(([key, value]) => {
      setHeader(config.headers, key, value);
    });
  }

  return config;
});

instance.interceptors.response.use(
  async (res) => {
    await applySecretResponseHeaders(res.headers);

    if (Number(res.data?.code) === 100060 && !res.config?._signRetry) {
      res.config._signRetry = true;
      return instance(res.config);
    }

    promptLoginOnce(res.data);
    return res.data;
  },
  async (err) => {
    const { response } = err;
    if (!response) return Promise.reject(err);

    await applySecretResponseHeaders(response.headers);
    promptLoginOnce(response.data);

    const errCodes = [401, 403, 500];
    const code = response.status;
    notifyServer('warning', Msgs[code] || 'Request failed', 'Warning');
    if (errCodes.includes(response.status)) {
      return Promise.reject(err);
    }
    return Promise.reject(err);
  },
);

const http = {
  get: (url = '', params = {}) => instance.get(url, { params }),
  post: (url = '', data = {}, config = {}) => instance.post(url, data, config),
  put: (url = '', data = {}) => instance.put(url, data),
  delete: (url = '', data = {}) => instance.delete(url, data),
  patch: (url = '', data = {}) => instance.patch(url, data),
};

export default http;
